import React from 'react';
import { sanitize } from 'helpers/utils/htmlSanitize';
import Typography from 'components/commercetools-ui/atoms/typography';
import useMediaQuery from 'helpers/hooks/useMediaQuery';
import { mediumDesktop, hugeDesktop, largeDesktop, semiLargeDesktop } from 'helpers/utils/screensizes';

import { GenericSectionContent, MediaCard } from '../../types';
import { useAdditionalCss } from '../../../helpers/useAdditionalCss';
import VideoFile, { MOBILE_BREAKPOINT } from './video-file';
import VimeoVideo from './vimeo-video';
import styles from './index.module.css';

const VIDEO_FILE_EXTENSIONS = /\.(mp4|webm|ogv|ogg|mov|m4v)$/i;

//A hosted file has to be played by a real <video> element: dropping it into an iframe hands
//playback to the browser's built-in media viewer, which we cannot drive or unmute. Anything else
//is treated as a Vimeo embed - the only provider this rendering supports.
const isDirectVideoFile = (url: string) => {
  try {
    return VIDEO_FILE_EXTENSIONS.test(new URL(url, 'https://localhost').pathname);
  } catch {
    return false;
  }
};

export default function VortexRendering({ content }: { content: GenericSectionContent }) {
  const { cards, title, description, content_alignment, additional_css, additional_css_class_names } = content;

  const { uniqueClass, scopedAdditionalCss } = useAdditionalCss(additional_css, 'VortexRendering');

  const textAlignmentClass =
    content_alignment === 'Left' ? 'text-left' :
    content_alignment === 'Right' ? 'text-right' :
    'text-center';

  //An iframe cannot pick its own source off a media query the way <source media> lets a <video>,
  //so the phone cut has to be chosen here. Width is 0 until the hook's effect runs, and desktop is
  //assumed until then: that keeps the server-rendered markup and the first client render in step,
  //and leaves the one-off swap to phones, where the idle player has nothing to lose by reloading.
  const [viewportWidth] = useMediaQuery();
  const isMobileViewport = viewportWidth > 0 && viewportWidth < MOBILE_BREAKPOINT;

  const [isHugeDesktopSize] = useMediaQuery(hugeDesktop);
  const [isSemiLargeDesktopSize] = useMediaQuery(semiLargeDesktop);
  const [ismediumDesktopSize] = useMediaQuery(mediumDesktop);
  const [isLargeDesktopSize] = useMediaQuery(largeDesktop);

  const iframeHeight =
    isHugeDesktopSize ? '720px' :
    isSemiLargeDesktopSize ? '480px' :
    ismediumDesktopSize ? '500px' :
    isLargeDesktopSize ? '600px' :
    '320px';

  return (
    <>
      {scopedAdditionalCss && <style dangerouslySetInnerHTML={{ __html: scopedAdditionalCss }} />}
      <div className={`pt-24 pb-24 lg:pt-40 lg:pb-40 2xl:pt-60 2xl:pb-60 ${[additional_css_class_names, uniqueClass].filter(Boolean).join(' ')}`}>
        {title && (
          <Typography
            as="h3"
            className={`mb-36 md:mb-48 text-32 xl:text-36 2xl:text-48 font-medium leading-8 xl:leading-[2.25rem] 2xl:leading-[3.25rem] text-[#090909] ${textAlignmentClass}`}
          >
            {title}
          </Typography>
        )}
        {description && (
          <div
            className={`mb-24 text-16 md:text-18 2xl:text-20 font-normal leading-6 text-[#333] ${textAlignmentClass}`}
            dangerouslySetInnerHTML={{ __html: sanitize(description) }}
          />
        )}
        {cards?.map((card: MediaCard, index: number) => {
          const desktopUrl = card?.external_media_url;

          if (!desktopUrl) return null;

          const mobileUrl = card.mobile_external_media_url;
          //`||`, not `??`: the CMS sends an empty string for an untitled card, which would leave
          //the iframe/video with a blank accessible name.
          const title = card?.title || `Video ${index + 1}`;

          //Both cuts have to be files before <video> can switch between them itself. A card that
          //pairs a file with an embed is not something the CMS should produce, but rather than
          //hand one component the other's URL, fall back to picking a single one per viewport.
          const bothAreFiles = isDirectVideoFile(desktopUrl) && !!mobileUrl && isDirectVideoFile(mobileUrl);
          const activeUrl = (isMobileViewport && mobileUrl) || desktopUrl;

          return (
            <div
              key={index}
              className="video-container flex justify-center md:px-100 2xl:px-208"
            >
              {isDirectVideoFile(activeUrl) ? (
                <VideoFile
                  src={bothAreFiles ? desktopUrl : activeUrl}
                  mobileSrc={bothAreFiles ? mobileUrl : undefined}
                  height={iframeHeight}
                  poster={(isMobileViewport ? card.mobile_image?.url : undefined) ?? card.desktop_image?.url}
                  title={title}
                />
              ) : (
                <VimeoVideo src={activeUrl} height={iframeHeight} title={title} />
              )}
            </div>
          );
        })}
      </div>
    </>
  );
}
